import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import {
  BulkSheetFilter,
  FilterSelect,
  FilterInput,
  FilterFooter,
} from '../common'
import {
  NumberFilter,
  NumberFilterOperator,
  NumberFilterOperatorLabel,
} from '../NumberFilter'
import { useSelector } from 'react-redux'
import usePrevious from '../../../../../hooks/usePrevious'
import { AllState } from '../../../../../../store'

type Props = CustomFilterProps & {
  fetch: (v: NumberFilter) => Promise<APIResponse>
}

const BLANK_OPERATORS = [
  NumberFilterOperator.BLANK,
  NumberFilterOperator.NOT_BLANK,
]

const buildNumberFilterModel = (
  operator: NumberFilterOperator,
  value: number | undefined
): NumberFilter | null => {
  const isBlankOp = BLANK_OPERATORS.includes(operator)
  const isActive = isBlankOp || value !== undefined
  return isActive
    ? {
        operator,
        value: isBlankOp
          ? undefined
          : Number.isFinite(value)
            ? value
            : undefined,
      }
    : null
}

export const ServerSideNumberFilter = ({
  model,
  onModelChange,
  api,
  fetch: fetchFn,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const operator =
    (model?.operator as NumberFilterOperator) ?? NumberFilterOperator.EQUALS
  const value = model?.value as number | undefined

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [isComposing, setIsComposing] = useState<boolean>(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: NumberFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: NumberFilter | null) => {
        if (isComposing) return
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(model as NumberFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as NumberFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    afterGuiAttached() {
      inputRef.current?.focus()
    },

    getModelAsString() {
      if (!model) return ''
      if (BLANK_OPERATORS.includes(operator)) {
        return `(${operator.charAt(0)}${_.lowerCase(operator.slice(1))})`
      }
      const symbolMap: Record<string, string> = {
        [NumberFilterOperator.EQUALS]: '=',
        [NumberFilterOperator.NOT_EQUALS]: '≠',
        [NumberFilterOperator.LESS_THAN]: '<',
        [NumberFilterOperator.LESS_THAN_OR_EQUALS]: '≤',
        [NumberFilterOperator.GREATER_THAN]: '>',
        [NumberFilterOperator.GREATER_THAN_OR_EQUALS]: '≥',
      }
      return `${symbolMap[operator] ?? ''} ${value}`
    },
  })

  return (
    <BulkSheetFilter>
      <FilterSelect
        variant="outlined"
        value={operator}
        onChange={e =>
          onModelChange(
            buildNumberFilterModel(
              e.target.value as NumberFilterOperator,
              value
            )
          )
        }
        sx={{ width: '100%' }}
      >
        {/* TODO Use select component to customize style */}
        {Object.values(NumberFilterOperator).map(v => (
          <option key={`text-filter-option-${v}`} value={v}>
            {NumberFilterOperatorLabel[v]}
          </option>
        ))}
      </FilterSelect>
      <FilterInput
        type="number"
        inputRef={inputRef}
        value={value ?? ''}
        onChange={e => {
          const num = parseFloat(e.target.value)
          onModelChange(
            buildNumberFilterModel(
              operator,
              Number.isFinite(num) ? num : undefined
            )
          )
        }}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={e => {
          setIsComposing(false)
          const num = parseFloat((e.target as HTMLInputElement).value)
          onModelChange(
            buildNumberFilterModel(
              operator,
              Number.isFinite(num) ? num : undefined
            )
          )
        }}
        placeholder={'Filter...'}
        sx={{ height: 22 }}
      />
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
